import React, { useState, useEffect } from "react";
import { Button, styled, Box, Typography } from "@mui/material";
import { useLazyQuery } from "@apollo/client";
import { GET_GSC_SITES } from "../../gql/queries/gsc";
import { GET_KEYWORDS } from "../../gql/queries/analyze-keyword-gsc";

const KeywordRankingsWrapper = styled("section")(({ theme }) => ({
  marginTop: "20px",

  "& .rankingsCard": {
    backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "6px",
    padding: "24px",
  },

  "& h2": {
    fontSize: "20px",
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: "20px",
  },

  "& .formGroup": {
    marginBottom: "18px",

    "& label": {
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginBottom: "6px",
    },

    "& input, & select": {
      width: "100%",
      padding: "10px 12px",
      borderRadius: "6px",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      fontSize: "14px",

      "&:focus": {
        outline: "none",
        borderColor: theme.palette.primary.main,
      },
    },

    "& select:disabled": {
      backgroundColor: theme.palette.action.disabledBackground,
      cursor: "not-allowed",
    },
  },

  "& .formDivider": {
    display: "flex",
    alignItems: "center",
    margin: "20px 0",

    "&::before, &::after": {
      content: '""',
      flex: 1,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },

    "& span": {
      padding: "0 12px",
      fontSize: "12px",
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
  },

  "& .helpText": {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    marginTop: "4px",
    fontStyle: "italic",
  },

  "& .loadingText": {
    padding: "12px",
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "13px",
  },

  "& .submitBtn": {
    marginTop: "10px",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",

    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },

    "&.Mui-disabled": {
      opacity: 0.4,
    },
  },

  "& .errorBox": {
    marginTop: "20px",
    padding: "12px",
    borderRadius: "6px",
    backgroundColor: theme.palette.mode === "dark" ? "#4a1f24" : "#f8d7da",
    color: theme.palette.mode === "dark" ? "#ffb3b3" : "#721c24",
    whiteSpace: "pre-line",
    fontSize: "13px",
  },

  "& .results": {
    marginTop: "30px",
  },

  "& .resultsHeader": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "16px",

    "& h3": {
      fontSize: "18px",
      fontWeight: 700,
      color: theme.palette.text.primary,
    },

    "& .info": {
      display: "flex",
      gap: "16px",
      fontSize: "12px",
      color: theme.palette.text.secondary,
    },
  },

  "& table": {
    width: "100%",
    borderCollapse: "collapse",

    "& thead": {
      backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f8f9fa",
    },

    "& th": {
      padding: "10px",
      textAlign: "left",
      fontSize: "13px",
      fontWeight: 600,
      color: theme.palette.text.primary,
      borderBottom: `2px solid ${theme.palette.divider}`,
    },

    "& td": {
      padding: "10px",
      fontSize: "13px",
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },

    "& tbody tr:hover": {
      backgroundColor: theme.palette.action.hover,
    },

    "& .keywordCell": {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
  },

  "& .noResults": {
    textAlign: "center",
    padding: "40px",
    color: theme.palette.text.secondary,
    fontSize: "14px",
  },

  "& .resultsFooter": {
    marginTop: "12px",
    padding: "10px",
    textAlign: "center",
    fontSize: "12px",
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    borderRadius: "6px",
  },
}));

function KeywordRankings({ email, defaultSite }) {
  const [url, setUrl] = useState("");
  const [filterType, setFilterType] = useState("url");
  const [loading, setLoading] = useState(false);
  const [keywords, setKeywords] = useState(null);
  const [error, setError] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [loadingSites, setLoadingSites] = useState(true);

  const [loadKeywords] = useLazyQuery(GET_KEYWORDS, { fetchPolicy: "no-cache" });

  const [loadSites] = useLazyQuery(GET_GSC_SITES, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (data?.gscSites?.success) {
        const fetchedSites = data.gscSites.sites || [];
        setSites(fetchedSites);
        if (fetchedSites.length) {
          setSelectedSite(fetchedSites[0].siteUrl);
        }
      }
      setLoadingSites(false);
    },
    onError: () => setLoadingSites(false),
  });

  useEffect(() => {
    if (email) loadSites({ variables: { email } });
    else setLoadingSites(false);
  }, [email, loadSites]);

  useEffect(() => {
    if (defaultSite) {
      setSelectedSite(defaultSite);
      setUrl(defaultSite);
      setFilterType("domain");
    }
  }, [defaultSite]);

  const handleSubmit = async e => {
    e.preventDefault();

    const targetUrl = selectedSite || url.trim();
    if (!targetUrl) {
      setError("Please select a domain or enter a URL");
      return;
    }

    setLoading(true);
    setError(null);
    setKeywords(null);

    try {
      const { data } = await loadKeywords({
        variables: {
          url: targetUrl,
          filter_type: filterType,
          limit: 1000,
          email: email || null,
        },
      });

      if (data?.keywords?.success) setKeywords(data.keywords);
      else setError("Failed to fetch keyword rankings");
    } catch (err) {
      setError(err?.graphQLErrors?.[0]?.message || "Failed to fetch keyword rankings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeywordRankingsWrapper>
      <Box className="rankingsCard">
        <Typography variant="h2">Keyword Rankings</Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Box className="formGroup">
            <Typography component="label">Select Domain (from GSC)</Typography>
            {loadingSites ? (
              <Box className="loadingText">Loading domains...</Box>
            ) : (
              <Box component="select" value={selectedSite} onChange={e => setSelectedSite(e.target.value)}>
                <option value="">-- Select a domain --</option>
                {sites.map(site => (
                  <option key={site.siteUrl} value={site.siteUrl}>
                    {site.displayUrl || site.siteUrl}
                  </option>
                ))}
              </Box>
            )}
          </Box>

          <Box className="formDivider">
            <Typography component="span">OR</Typography>
          </Box>

          <Box className="formGroup">
            <Typography component="label">Enter URL or Domain</Typography>
            <Box
              component="input"
              value={url}
              onChange={e => {
                setUrl(e.target.value);
                setSelectedSite("");
              }}
              placeholder="https://example.com"
            />
          </Box>

          <Box className="formGroup">
            <Typography component="label">Analysis Type</Typography>
            <Box component="select" value={filterType} onChange={e => setFilterType(e.target.value)} disabled={!!selectedSite}>
              <option value="url">Specific URL</option>
              <option value="domain">Domain-wide</option>
            </Box>
            {selectedSite && <Box className="helpText">Domain-wide analysis is used when a domain is selected</Box>}
          </Box>

          <Button type="submit" className="submitBtn" disabled={loading}>
            {loading ? "Fetching Keywords..." : "Fetch Rankings"}
          </Button>
        </Box>

        {error && <Box className="errorBox">{error}</Box>}

        {keywords && (
          <Box className="results">
            <Box className="resultsHeader">
              <Typography variant="h3">Results</Typography>
              <Box className="info">
                <Typography component="span">Total: {keywords.total_keywords}</Typography>
                <Typography component="span">{keywords.filter_type}</Typography>
              </Box>
            </Box>

            {keywords.keywords.length ? (
              <>
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Keyword</th>
                      <th>Volume</th>
                      <th>Position</th>
                      <th>Traffic</th>
                      <th>CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.keywords.map((k, i) => (
                      <tr>
                        <td>{i + 1}</td>
                        <td className="keywordCell">{k.keyword}</td>
                        <td>{k.search_volume}</td>
                        <td>{k.position}</td>
                        <td>{k.traffic}</td>
                        <td>{k.ctr}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <Box className="resultsFooter">
                  Showing {keywords.keywords.length} of {keywords.total_keywords}
                </Box>
              </>
            ) : (
              <Box className="noResults">No keyword data found</Box>
            )}
          </Box>
        )}
      </Box>
    </KeywordRankingsWrapper>
  );
}

export default KeywordRankings;
